import axios from 'axios'

// Base API URL - dynamically determined from current hostname
const baseURL = process.env.VUE_APP_API_BASE_URL || 
  ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:8000/api'
    : 'https://precious-madeleine-illyra-hcetqms-finapp-b8c92782.koyeb.app/api');

// Request timeout in milliseconds
const REQUEST_TIMEOUT = 30000

// Create axios instance with default config
const api = axios.create({
  baseURL,
  timeout: REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: false // Disable credentials for cross-origin requests
})

// Request interceptor - add token to requests
api.interceptors.request.use(
  config => {
    // Skip authentication for these public endpoints
    const publicEndpoints = [
      '/login',
      '/transaction-types', // All transaction types endpoints
      '/students/id-number',
      '/kiosk', // Match all kiosk routes
      // Public display data endpoints only (NOT admin display management)
      '/displays/active-transactions',
      '/displays/pending-transactions'
    ];

    // Extract the path part from the full URL for matching
    const urlPath = config.url.replace(api.defaults.baseURL, '');

    // Also treat GET /displays/:id/data as public
    const isPublicDisplayData = /^\/displays\/\d+\/data$/.test(urlPath);

    // Special case: GET /settings/audio is public, but PUT/POST require auth
    const isPublicAudioSettings = urlPath === '/settings/audio' && config.method === 'get';

    // Exception paths that should always require authentication
    const protectedExceptions = [
      '/transaction-types/\\d+/flow-config' // Flow config endpoint always needs auth
    ];

    // Check if this is a public endpoint - more accurate matching
    const isPublicEndpoint = publicEndpoints.some(endpoint =>
      urlPath === endpoint || urlPath.startsWith(endpoint + '/')
    ) || isPublicDisplayData || isPublicAudioSettings;

    // Check if this is a protected exception that should always have auth
    const isProtectedException = protectedExceptions.some(pattern => {
      const regex = new RegExp(pattern);
      return regex.test(urlPath); // Match against urlPath instead of config.url
    });

    // Add token if it's a protected exception OR if it's NOT a public endpoint
    if (isProtectedException || !isPublicEndpoint) {
      const token = localStorage.getItem('auth_token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
        //console.log('Adding auth token to request:', config.url)
      } else {
        console.warn('No auth token found for protected endpoint:', config.url)
      }
    }

    return config
  },
  error => {
    console.error('Request interceptor error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor - handle common errors
api.interceptors.response.use(
  response => {
    return response
  },
  error => {
    // Handle 401 Unauthorized globally, but don't redirect for transaction-type flow config
    if (error.response && error.response.status === 401) {
      // Check if this is the transaction type flow config endpoint
      const isFlowConfigEndpoint = error.config && error.config.url &&
        error.config.url.includes('/transaction-types/') &&
        error.config.url.includes('/flow-config');

      if (!isFlowConfigEndpoint) {
        // Import store dynamically to avoid circular dependencies
        import('@/store').then(store => {
          store.default.dispatch('auth/logout')
        })

        // Only redirect if not already on login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
      } else {
        console.error('Authentication error when saving flow config:', error.response?.data || error.message);
      }
    }

    return Promise.reject(error)
  }
)

// API module for authentication
export const authApi = {
  login(username, password) {
    return api.post('/login', { username, password })
  },

  logout() {
    return api.post('/logout')
  },

  getCurrentUser() {
    return api.get('/user')
  }
}

// API module for users
export const usersApi = {
  getUsers() {
    return api.get('/users')
  },

  getUser(id) {
    return api.get(`/users/${id}`)
  },

  createUser(userData) {
    return api.post('/users', userData)
  },

  updateUser(id, userData) {
    return api.put(`/users/${id}`, userData)
  },

  deleteUser(id) {
    return api.delete(`/users/${id}`)
  },

  getRoles() {
    return api.get('/roles')
  },

  checkUsername(username, excludeId = null) {
    return api.post('/users/check-username', {
      username,
      exclude_id: excludeId
    })
  },

  adminChangePassword(userId, password) {
    return api.post(`/users/${userId}/password`, { password })
  }
}

// API module for transactions
export const transactionsApi = {
  getTransactions(options = {}) {
    return api.get('/transactions', { params: options });
  },

  getTransaction(id) {
    return api.get(`/transactions/${id}`);
  },

  createTransaction(transactionData) {
    return api.post('/transactions', transactionData);
  },

  updateStatus(id, statusData) {
    // Allow passing either a string status or an object with status and other fields
    const data = typeof statusData === 'string'
      ? { status: statusData }
      : statusData;

    return api.put(`/transactions/${id}/status`, data);
  },

  updateTransaction(id, data) {
    return api.put(`/transactions/${id}`, data);
  },

  toggleSkipFlag(id) {
    return api.put(`/transactions/${id}/skip-flag`);
  },

  getTypes() {
    return api.get('/transaction-types')
      .then(response => {
        // Ensure is_active is boolean for each type
        if (response.data) {
          response.data = response.data.map(type => ({
            ...type,
            is_active: Boolean(type.is_active)
          }));
        }
        return response;
      });
  },

  getActiveTypes() {
    return api.get('/transaction-types')
      .then(response => {
        // Ensure is_active is boolean for each type and filter active ones
        if (response.data) {
          response.data = response.data
            .map(type => ({
              ...type,
              is_active: Boolean(type.is_active)
            }))
            .filter(type => type.is_active);
        }
        return response;
      });
  },

  getType(id) {
    return api.get(`/transaction-types/${id}`)
      .then(response => {
        // Ensure is_active is boolean
        if (response.data) {
          response.data = {
            ...response.data,
            is_active: Boolean(response.data.is_active)
          };
        }
        return response;
      });
  },

  createType(typeData) {
    // Ensure is_active is properly sent as a boolean
    const data = {
      ...typeData,
      is_active: typeData.is_active === true
    };

    return api.post('/transaction-types', data);
  },

  updateType(id, typeData) {
    // Ensure is_active is properly sent as a boolean
    const data = {
      ...typeData,
      is_active: typeData.is_active === true
    };

    return api.put(`/transaction-types/${id}`, data);
  },

  deleteType(id) {
    return api.delete(`/transaction-types/${id}`);
  },

  // Transaction-specific flow configuration
  saveTypeFlowConfig(typeId, flowConfigData) {
    console.log(`Saving flow config for transaction type ${typeId}:`, flowConfigData);

    return api.put(`/transaction-types/${typeId}/flow-config`, flowConfigData)
      .then(response => {
        console.log('Flow config saved successfully:', response.data);
        return response;
      })
      .catch(error => {
        console.error('Error saving flow config:', error.response?.data || error.message);
        throw error;
      });
  },

  // Transaction Requirements
  getTypeRequirements(typeId) {
    return api.get(`/transaction-types/${typeId}/requirements`);
  },

  createRequirement(requirementData) {
    return api.post('/transaction-requirements', requirementData);
  },

  updateRequirement(id, requirementData) {
    return api.put(`/transaction-requirements/${id}`, requirementData);
  },

  deleteRequirement(id) {
    return api.delete(`/transaction-requirements/${id}`);
  },

  // Transaction Type Settings
  getTypeSettings(typeId) {
    return api.get(`/transaction-requirements/type/${typeId}/settings`);
  },

  updateTypeSettings(typeId, settingsData) {
    return api.put(`/transaction-requirements/type/${typeId}/settings`, settingsData);
  },

  // Kiosk-specific transaction methods
  createKioskTransaction(transactionData) {
    return api.post('/kiosk/transactions', transactionData)
  },

  printTicket(transactionId) {
    return api.post(`/kiosk/transactions/${transactionId}/print`)
  }
}

// API module for tellers
export const tellersApi = {
  getTellers() {
    return api.get('/tellers')
  },

  getCurrentTeller() {
    return api.get('/tellers/current')
  },

  getQueue(id) {
    // Include today's date in the request to ensure proper filtering
    const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
    return api.get(`/tellers/${id}/queue`, {
      params: { date: today }
    });
  },

  // callNext has been updated to accept skippedIds as second parameter
  callNext(id, skippedIds = []) {
    const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
    console.log(`Calling next for teller ${id}, date: ${today}, skippedIds:`, skippedIds);

    return api.post(`/tellers/${id}/call-next`, {
      date: today,
      skipped_ids: skippedIds
    });
  },

  completeCurrent(id, data = {}) {
    return api.post(`/tellers/${id}/complete-current`, data)
  },

  skipCurrent(id) {
    return api.post(`/tellers/${id}/skip-current`)
  },

  repeatCall(transactionId) {
    return api.post(`/transactions/${transactionId}/repeat-call`);
  },

  updateCurrentTransaction(tellerId, transactionId, additionalData = {}) {
    // If called with an object instead of an ID, treat it as additionalData
    if (typeof transactionId === 'object') {
      additionalData = transactionId;
      transactionId = null;
    }

    const endpoint = transactionId
      ? `/tellers/${tellerId}/current-transaction/${transactionId}`
      : `/settings/teller`;

    return api.post(endpoint, additionalData);
  },

  deleteTeller(id) {
    return api.delete(`/tellers/${id}`)
  },

  fixAssignments() {
    return api.post('/tellers/fix-assignments')
  },

  // Fetch teller settings (including transaction_types_handled)
  getTellerSettings(tellerId) {
    return api.get(`/tellers/${tellerId}/settings`);
  },

  // Save teller settings (including transaction_types_handled)
  saveSettings(settingsData) {
    return api.post('/settings/teller', settingsData); // Corrected endpoint
  },

  // Teller dashboard and reporting endpoints
  getDashboardStats() {
    return api.get('/tellers/dashboard/stats');
  },

  getPerformanceReport(params = {}) {
    return api.get('/tellers/performance-report', { params });
  },

  exportTransactions(params = {}) {
    return api.get('/tellers/export-transactions', {
      params,
      responseType: 'blob'
    });
  },

  getRecentActivity(params = {}) {
    return api.get('/tellers/recent-activity', { params });
  }
}

// API module for students
export const studentsApi = {
  getStudents() {
    return api.get('/students', { params: { per_page: 5000 } })
  },

  getStudent(id) {
    return api.get(`/students/${id}`)
  },

  getStudentByIdNumber(idNumber) {
    return api.get(`/students/id-number/${idNumber}`)
  },

  createStudent(studentData) {
    return api.post('/students', studentData)
  },

  updateStudent(id, studentData) {
    return api.put(`/students/${id}`, studentData)
  },

  deleteStudent(id) {
    return api.delete(`/students/${id}`)
  },

  getTransactionHistory(id) {
    return api.get(`/students/${id}/transactions`)
  },

  importStudents(fileData) {
    const formData = new FormData()
    formData.append('file', fileData)

    return api.post('/students/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 300000 // 5 minutes for large imports
    })
  }
}

export const dashboardApi = {
  getStats() {
    return api.get('/admin/system-stats')
      .catch(error => {
        // If endpoint not found, return null
        if (error.response && error.response.status === 404) {
          console.warn('Admin system stats endpoint not found')
          return { data: null }
        }
        throw error
      })
  },

  getRecentActivity(limit = 10) {
    return api.get(`/admin/system-activity`, { params: { limit } })
      .catch(error => {
        // If endpoint not found, return null
        if (error.response && error.response.status === 404) {
          console.warn('Admin system activity endpoint not found')
          return { data: null }
        }
        throw error
      })
  }
}

// API module for queue displays
export const displaysApi = {
  getDisplays() {
    return api.get('/displays')
  },
  getMediaFiles() {
    return api.get('/display-media')
  },
  uploadMedia(formData, onUploadProgress) {
    return api.post('/display-media', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress
    })
  },
  saveMediaOrder(mediaOrder) {
    return api.put('/display-media/order', { media: mediaOrder })
  },
  toggleMedia(id) {
    return api.put(`/display-media/${id}/toggle`)
  },
  deleteMedia(id) {
    return api.delete(`/display-media/${id}`)
  },

  getDisplayData(id) {
    return api.get(`/displays/${id}/data`)
      .catch(error => {
        // Check if this is a network error or 404
        if (!error.response || error.response.status === 404) {
          console.warn('Display data API endpoint not available. Backend may not be running.');
          throw new Error('Could not connect to backend API. The server might not be running.');
        }
        throw error;
      })
  },

  getPendingTransactions() {
    return api.get('/displays/pending-transactions')
  },

  getActiveTransactions() {
    return api.get('/displays/active-transactions')
      .catch(error => {
        // Check if this is a network error or 404
        if (!error.response || error.response.status === 404) {
          console.warn('Active transactions API endpoint not available. Backend may not be running.');
          throw new Error('Could not connect to backend API. The server might not be running.');
        }
        throw error;
      })
  },

  // New methods for display management
  createDisplay(displayData) {
    return api.post('/displays', displayData)
  },

  updateDisplay(id, displayData) {
    return api.put(`/displays/${id}`, displayData)
  },

  deleteDisplay(id) {
    return api.delete(`/displays/${id}`)
  }
}

// API module for settings
export const settingsApi = {
  // General settings
  getGeneralSettings() {
    return api.get('/settings/general');
  },

  saveGeneralSettings(settingsData) {
    return api.post('/settings/general', settingsData);
  },

  // Teller settings
  getTellerSettings() {
    return api.get('/settings/teller');
  },

  saveTellerSettings(settingsData) {
    return api.post('/settings/teller', settingsData);
  },

  // Kiosk settings
  getKioskSettings() {
    return api.get('/settings/kiosk');
  },

  saveKioskSettings(settingsData) {
    return api.post('/settings/kiosk', settingsData);
  },

  // Display settings
  getDisplaySettings() {
    return api.get('/settings/display');
  },

  saveDisplaySettings(settingsData) {
    return api.post('/settings/display', settingsData);
  },

  // Audio settings
  getAudioSettings() {
    return api.get('/settings/audio');
  },

  updateAudioSettings(settingsData) {
    return api.put('/settings/audio', settingsData);
  },

  uploadCustomTone(file) {
    const formData = new FormData();
    formData.append('tone', file);

    return api.post('/settings/audio/tone-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
}

// Audio Settings API
export const audioSettingsApi = {
  getAudioSettings() {
    return api.get('/settings/audio');
  },

  updateAudioSettings(settingsData) {
    return api.put('/settings/audio', settingsData);
  },

  uploadCustomTone(file) {
    const formData = new FormData();
    formData.append('tone', file);

    return api.post('/settings/audio/tone-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
}

// Priority Codes API
export const priorityCodesApi = {
  getPriorityCodes() {
    return api.get('/priority-codes')
      .then(response => {
        console.log('Priority codes response:', response.data);
        return response;
      })
      .catch(error => {
        console.error('Error fetching priority codes:', error.response?.data || error);
        throw error;
      });
  },

  createPriorityCode(data) {
    console.log('Creating priority code with data:', data);
    return api.post('/priority-codes', data)
      .then(response => {
        console.log('Create priority code response:', response.data);
        return response;
      })
      .catch(error => {
        console.error('Error creating priority code:', error.response?.data || error);
        throw error;
      });
  },

  updatePriorityCode(id, data) {
    console.log('Updating priority code:', id, data);
    return api.put(`/priority-codes/${id}`, data)
      .then(response => {
        console.log('Update priority code response:', response.data);
        return response;
      })
      .catch(error => {
        console.error('Error updating priority code:', error.response?.data || error);
        throw error;
      });
  },

  deletePriorityCode(id) {
    console.log('Deleting priority code:', id);
    return api.delete(`/priority-codes/${id}`)
      .then(response => {
        console.log('Delete priority code response:', response.data);
        return response;
      })
      .catch(error => {
        console.error('Error deleting priority code:', error.response?.data || error);
        throw error;
      });
  },

  verifyPriorityCode(code) {
    console.log('Verifying priority code:', code);
    return api.post('/priority-codes/verify', { code })
      .then(response => {
        console.log('Verify priority code response:', response.data);
        return response;
      })
      .catch(error => {
        console.error('Error verifying priority code:', error.response?.data || error);
        throw error;
      });
  }
}

// API module for kiosk flow
export const kioskFlowApi = {
  getFlowConfig() {
    return api.get('/kiosk/flow-config')
      .catch(error => {
        console.error('Error getting kiosk flow config:', error);
        throw error;
      });
  },

  saveFlowConfig(flowConfig) {
    console.log('Saving kiosk flow config:', flowConfig);
    return api.post('/settings/kiosk-flow', flowConfig)
      .catch(error => {
        console.error('Error saving kiosk flow config:', error.response?.data || error);
        throw error;
      });
  }
}

// API module for admin reports and analytics
export const adminApi = {
  getSystemStats() {
    return api.get('/admin/system-stats');
  },

  getSystemReport(params = {}) {
    return api.get('/admin/system-report', { params });
  },

  exportSystemTransactions(params = {}) {
    return api.get('/admin/export-system-transactions', {
      params,
      responseType: 'blob'
    });
  },

  getSystemActivity(params = {}) {
    return api.get('/admin/system-activity', { params });
  }
}

// Export default as consolidated object (for backward compatibility)
export default {
  auth: authApi,
  users: usersApi,
  transactions: transactionsApi,
  tellers: tellersApi,
  students: studentsApi,
  dashboard: dashboardApi,
  displays: displaysApi,
  settings: settingsApi,
  audio: audioSettingsApi,
  priorityCodes: priorityCodesApi,
  kioskFlow: kioskFlowApi,
  admin: adminApi
} 